import type { SchemaState } from './schema-editor-types';

export function SchemaReview(props: {
  state: SchemaState;
  busy: boolean;
  dirty: boolean;
  canManage: boolean;
  onSave: () => Promise<void>;
  onReset: () => void;
  onPublish: () => Promise<void>;
}): React.JSX.Element {
  const issueRows: React.JSX.Element[] = [];
  for (const issue of props.state.validationIssues) {
    issueRows.push(<div className="schema-issue-row" key={`${issue.pointer}:${issue.message}`}><code>{issue.pointer}</code><span>{issue.message}</span></div>);
  }
  const changedVersions: React.JSX.Element[] = [];
  for (const version of props.state.eventVersions) {
    if (!version.changed) {
      continue;
    }
    const event = findEvent(props.state, version.eventId);
    changedVersions.push(<div className="definition-row" key={version.eventId}><span><strong>{event?.displayName || event?.eventKey || 'New event'}</strong><small>{event?.eventKey || version.eventId}</small></span><span><strong>v{version.version}</strong><small>contract change</small></span></div>);
  }
  const canPublish = !props.dirty && props.state.preview !== null && props.state.validationIssues.length === 0;

  return (
    <section className="schema-review-grid">
      <article className="surface schema-review-main">
        <div className="surface-heading">
          <div><span className="eyebrow">Publication gate</span><h2>Review schema</h2></div>
          <span className={props.state.validationIssues.length === 0 ? 'schema-valid-badge' : 'schema-invalid-badge'}>{props.state.validationIssues.length === 0 ? 'Valid' : `${props.state.validationIssues.length} issues`}</span>
        </div>
        {props.dirty && <div className="schema-review-warning">Save your current editor changes before publishing. Server validation reflects the last saved draft.</div>}
        {issueRows.length === 0 ? <div className="schema-review-success">The saved draft is structurally valid and ready to publish.</div> : <div className="schema-issue-list">{issueRows}</div>}
        {props.canManage && (
          <div className="schema-editor-actions schema-review-actions">
            <span />
            <button className="secondary-button" disabled={props.busy || !props.dirty} onClick={props.onReset} type="button">Reset unsaved</button>
            <button className="secondary-button" disabled={props.busy || !props.dirty} onClick={props.onSave} type="button">Save draft</button>
            <button className="publish-button" disabled={props.busy || !canPublish} onClick={props.onPublish} type="button">{props.busy ? 'Working…' : 'Publish new version'}</button>
          </div>
        )}
      </article>

      <aside className="surface schema-version-preview">
        <div className="surface-heading"><div><span className="eyebrow">Automatic versions</span><h2>Event changes</h2></div><strong>{changedVersions.length}</strong></div>
        {changedVersions.length === 0 ? <p className="muted-copy">No event contract versions will change.</p> : changedVersions}
      </aside>

      <article className="surface schema-json-preview">
        <div className="surface-heading">
          <div><span className="eyebrow">Generated artifact</span><h2>Canonical JSON preview</h2></div>
          {props.state.preview !== null && <div className="schema-inline-actions"><button className="icon-button" onClick={copyJson} type="button">Copy</button><button className="icon-button" onClick={downloadJson} type="button">Download</button></div>}
        </div>
        {props.state.preview === null ? <p className="schema-empty-list">Resolve validation issues to generate the publishable schema.</p> : <pre>{formatPreview(props.state)}</pre>}
      </article>
    </section>
  );

  async function copyJson(): Promise<void> {
    if (props.state.preview !== null) {
      await navigator.clipboard.writeText(formatPreview(props.state));
    }
  }

  function downloadJson(): void {
    if (props.state.preview === null) {
      return;
    }
    const blob = new Blob([formatPreview(props.state)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `${props.state.preview.projectSlug}-${props.state.preview.environmentSlug}-schema.json`;
    anchor.click();
    URL.revokeObjectURL(url);
  }
}

function findEvent(state: SchemaState, eventId: string) {
  for (const event of state.document.events) {
    if (event.id === eventId) {
      return event;
    }
  }
  return undefined;
}

function formatPreview(state: SchemaState): string {
  return JSON.stringify(state.preview, null, 2);
}
